/**
 * Per-user persistence for the Intelligence dashboards.
 *
 * Layouts live in `user_preferences` (migration 101) under
 * `intel_dashboard_<page>`, the same mechanism the Marketing overview
 * uses — so no new table or RLS is needed and a layout built on a laptop
 * follows the person to the conference-room display. The stored JSON is
 * validated on read (`parseLayout`) so a widget pointing at a retired
 * source degrades gracefully.
 *
 * Writes are debounced: dragging a card around produces a burst of layout
 * changes and we want one upsert at the end, not twenty.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/AuthProvider';
import { emptyLayout, layoutsEqual, parseLayout } from './layout';
import type { DashboardLayout, DashboardPageKey } from './types';

export type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error';

const SAVE_DEBOUNCE_MS = 900;

export function dashboardPreferenceKey(page: DashboardPageKey): string {
  return `intel_dashboard_${page}`;
}

type LayoutUpdater = DashboardLayout | ((prev: DashboardLayout) => DashboardLayout);

export interface DashboardLayoutState {
  /** Null until the stored preference has been read. */
  layout: DashboardLayout | null;
  isLoading: boolean;
  /** True once the user has saved a layout of their own. */
  isCustomized: boolean;
  status: SaveStatus;
  saveError: string | null;
  editing: boolean;
  setLayout: (next: LayoutUpdater, opts?: { immediate?: boolean }) => void;
  startEditing: () => void;
  /** Keep the edits (flushes any pending save). */
  finishEditing: () => void;
  /** Throw away everything since `startEditing`. */
  discardEditing: () => void;
  resetToDefault: () => void;
  flush: () => void;
}

export function useDashboardLayout(
  page: DashboardPageKey,
  buildDefault: () => DashboardLayout,
  opts: { enabled?: boolean } = {},
): DashboardLayoutState {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const profileId = user?.id ?? null;
  const enabled = !!profileId && opts.enabled !== false;
  const prefKey = dashboardPreferenceKey(page);
  const queryKey = useMemo(() => ['user-preferences', prefKey, profileId] as const, [prefKey, profileId]);

  const query = useQuery<DashboardLayout | null>({
    queryKey,
    // Deferred until the caller knows the viewer's role: the default layout
    // is pruned by permission, so building it too early would leave it empty.
    enabled,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('user_preferences')
        .select('value')
        .eq('profile_id', profileId!)
        .eq('key', prefKey)
        .maybeSingle();
      if (error) throw error;
      return parseLayout(data?.value);
    },
  });

  const [local, setLocal] = useState<DashboardLayout | null>(null);
  const [status, setStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const snapshot = useRef<DashboardLayout | null>(null);
  const pending = useRef<DashboardLayout | null>(null);
  const timer = useRef<number | null>(null);
  const lastSaved = useRef<DashboardLayout | null>(null);

  // Adopt the stored layout (or the default) exactly once per page/user.
  useEffect(() => {
    setLocal(null);
    lastSaved.current = null;
  }, [prefKey, profileId]);

  useEffect(() => {
    if (!enabled || !query.isSuccess || local !== null) return;
    const stored = query.data;
    lastSaved.current = stored;
    setLocal(stored ?? buildDefault());
  }, [enabled, query.isSuccess, query.data, local, buildDefault]);

  const persist = useCallback(
    async (layout: DashboardLayout) => {
      if (!profileId) return;
      if (layoutsEqual(layout, lastSaved.current)) {
        setStatus('saved');
        return;
      }
      setStatus('saving');
      setSaveError(null);
      const { error } = await supabase
        .from('user_preferences')
        .upsert({ profile_id: profileId, key: prefKey, value: layout as never }, { onConflict: 'profile_id,key' });
      if (error) {
        setStatus('error');
        setSaveError(error.message);
        return;
      }
      lastSaved.current = layout;
      queryClient.setQueryData(queryKey, layout);
      setStatus('saved');
    },
    [profileId, prefKey, queryClient, queryKey],
  );

  const clearTimer = () => {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const flush = useCallback(() => {
    clearTimer();
    const next = pending.current;
    pending.current = null;
    if (next) void persist(next);
  }, [persist]);

  const setLayout = useCallback(
    (next: LayoutUpdater, opts?: { immediate?: boolean }) => {
      setLocal((prev) => {
        const resolved = typeof next === 'function' ? next(prev ?? emptyLayout()) : next;
        pending.current = resolved;
        return resolved;
      });
      setStatus('dirty');
      clearTimer();
      if (opts?.immediate) {
        // Defer one tick so the state updater above has run and `pending` is set.
        timer.current = window.setTimeout(flush, 0);
      } else {
        timer.current = window.setTimeout(flush, SAVE_DEBOUNCE_MS);
      }
    },
    [flush],
  );

  // Don't lose a trailing edit when the user navigates away mid-debounce.
  useEffect(() => () => flush(), [flush]);

  const startEditing = useCallback(() => {
    snapshot.current = local;
    setEditing(true);
  }, [local]);

  const finishEditing = useCallback(() => {
    setEditing(false);
    snapshot.current = null;
    flush();
  }, [flush]);

  const discardEditing = useCallback(() => {
    const restore = snapshot.current;
    setEditing(false);
    snapshot.current = null;
    if (restore) setLayout(restore, { immediate: true });
  }, [setLayout]);

  // Resetting deletes the stored preference rather than saving a copy of the
  // default, so the person keeps tracking future improvements to the default.
  const resetToDefault = useCallback(() => {
    clearTimer();
    pending.current = null;
    setLocal(buildDefault());
    if (!profileId) return;
    setStatus('saving');
    setSaveError(null);
    void supabase
      .from('user_preferences')
      .delete()
      .eq('profile_id', profileId)
      .eq('key', prefKey)
      .then(({ error }) => {
        if (error) {
          setStatus('error');
          setSaveError(error.message);
          return;
        }
        lastSaved.current = null;
        queryClient.setQueryData(queryKey, null);
        setStatus('saved');
      });
  }, [buildDefault, profileId, prefKey, queryClient, queryKey]);

  return {
    layout: local,
    isLoading: !enabled || query.isLoading || (query.isSuccess && local === null),
    isCustomized: query.data !== null && query.data !== undefined,
    status,
    saveError,
    editing,
    setLayout,
    startEditing,
    finishEditing,
    discardEditing,
    resetToDefault,
    flush,
  };
}
